import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProduct } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";
import { trackViewItem } from "../lib/analytics";
import { ProductGallery } from "../components/product/ProductGallery";
import { ColorPicker } from "../components/product/ColorPicker";
import { SizeSelector } from "../components/product/SizeSelector";
import { CompleteTheSetCard } from "../components/product/CompleteTheSetCard";
import { ProductDetailsAccordion } from "../components/product/ProductDetailsAccordion";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [error, setError] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    setError(false);
    getProduct(slug)
      .then((p) => {
        setProduct(p);
        setSize(p.sizes?.[1] || p.sizes?.[0] || "");
        setColor(p.colors?.[0] || "");
        trackViewItem(p);
      })
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="product-not-found">
        <h2 className="font-display text-4xl">This piece has retired.</h2>
        <Link to="/shop" className="inline-block mt-8 micro-label underline underline-offset-4">Return to shop</Link>
      </div>
    );
  }

  if (!product) {
    return <div className="py-32 text-center micro-label text-[#1a1814]/60" data-testid="product-loading">Loading…</div>;
  }

  const onAdd = () => {
    if (!size || !color) {
      toast.error("Please choose a size and colour.");
      return;
    }
    addItem(product, { size, color, quantity: 1 });
    toast.success(`${product.name} added to your bag.`);
  };

  const onAddSet = () => {
    if (!product.set?.partner) return;
    const partner = product.set.partner;
    if (!size || !color) {
      toast.error("Please choose a size and colour for this piece first.");
      return;
    }
    addItem(product, { size, color, quantity: 1 });
    const partnerSize = partner.sizes?.[1] || partner.sizes?.[0];
    const partnerColor = partner.colors?.[0];
    addItem(partner, { size: partnerSize, color: partnerColor, quantity: 1 });
    toast.success(`${product.set.name} added — saved £${product.set.discount.toFixed(2)}.`);
  };

  return (
    <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12 py-8 sm:py-12" data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-2 micro-label text-[#1a1814]/60 hover:text-[#1a1814] mb-6 sm:mb-8" data-testid="back-to-shop">
        <ChevronLeft className="w-3 h-3" /> Back to shop
      </Link>

      <div className="grid grid-cols-12 gap-6 lg:gap-16">
        <ProductGallery product={product} color={color} />

        <div className="col-span-12 lg:col-span-5 lg:sticky lg:top-28 self-start fade-up fade-up-1">
          <div className="micro-label text-[#1a1814]/60 mb-4 capitalize">{product.category}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1] text-[#1a1814]" data-testid="product-title">{product.name}</h1>
          <p className="italic font-serif text-lg text-[#1a1814]/70 mt-4">{product.tagline}</p>
          <div className="font-serif text-2xl mt-6" data-testid="product-price">£{product.price.toFixed(2)} <span className="text-xs text-[#1a1814]/50 ml-2">GBP</span></div>
          {product.embroidered && (
            <div className="mt-3 inline-flex items-center gap-2 micro-label text-[#1a1814]/70">
              <span className="inline-block w-1 h-1 rounded-full bg-[#1a1814]/60" />
              Finished with our tonal TIA embroidery
            </div>
          )}

          <p className="text-[#1a1814]/75 leading-relaxed mt-8">{product.description}</p>

          <ColorPicker colors={product.colors} value={color} onChange={setColor} />
          <SizeSelector sizes={product.sizes} value={size} onChange={setSize} />

          <button
            type="button"
            onClick={onAdd}
            className="mt-10 w-full bg-[#1a1814] text-[#f0e9e1] py-5 micro-label hover:bg-[#1a1814]/85"
            data-testid="add-to-cart-btn"
          >
            Add to Bag · £{product.price.toFixed(2)}
          </button>

          {product.set?.partner && (
            <CompleteTheSetCard set={product.set} onAddBoth={onAddSet} />
          )}

          <ProductDetailsAccordion fabric={product.fabric} fit={product.fit} />
        </div>
      </div>
    </div>
  );
}
